import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Boxes, Loader2, Search, Info, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getModelCatalog, getModelPlayground } from "@/lib/api";
import GuideLink from "@/components/GuideLink";
import {
  CATEGORY_NOTES, categoryLabel, detailErrorText, estimateText, filterModels, groupByCategory, normalizeModels, runAction,
} from "@/lib/modelHub";

export default function ModelHub() {
  const [state, setState] = useState({ status: "loading", models: [] });
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState({ status: "idle", data: null });

  useEffect(() => {
    let alive = true;
    getModelCatalog()
      .then((payload) => { if (alive) setState({ status: "ready", models: normalizeModels(payload) }); })
      .catch(() => { if (alive) setState({ status: "error", models: [] }); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!selected) { setDetail({ status: "idle", data: null }); return undefined; }
    let alive = true;
    setDetail({ status: "loading", data: null });
    getModelPlayground(selected)
      .then((payload) => { if (alive) setDetail({ status: "ready", data: payload?.model || null }); })
      .catch((error) => { if (alive) setDetail({ status: "error", data: null, message: detailErrorText(error) }); });
    return () => { alive = false; };
  }, [selected]);

  const visible = useMemo(() => filterModels(state.models, { category, query }), [state.models, category, query]);
  const groups = useMemo(() => groupByCategory(visible), [visible]);
  const action = runAction(detail.data);

  return (
    <div className="mx-auto max-w-5xl px-4 py-6" data-testid="model-hub">
      <div className="flex items-center gap-2">
        <Boxes className="h-6 w-6 text-slate-700" />
        <h1 className="text-xl font-semibold text-slate-900">Model Hub</h1>
        <GuideLink id="model-hub" className="ml-3" />
      </div>
      <p className="mt-1 text-sm text-slate-600" data-testid="hub-intro">
        Daftar model AI yang sudah siap dipakai. Model ditampilkan hanya setelah harga, adapter, dan aktivasi admin terverifikasi.
      </p>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari model…" className="pl-9"
                 maxLength={100} data-testid="hub-search" aria-label="Cari model" />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Kategori"
                className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="hub-category">
          <option value="all">Semua kategori</option>
          {["video", "image", "music", "audio", "voice", "chat"].map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
        </select>
      </div>

      {state.status === "loading" && (
        <div className="mt-6 flex items-center gap-2 text-sm text-slate-500" data-testid="hub-loading"><Loader2 className="h-4 w-4 animate-spin" /> Memuat model…</div>
      )}
      {state.status === "error" && (
        <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" role="alert" data-testid="hub-error">
          Katalog model tidak dapat dimuat saat ini. Tidak ada model yang dianggap tersedia.
        </div>
      )}

      {state.status === "ready" && groups.map((group) => {
        if (category !== "all" && group.category !== category) return null;
        return (
          <section key={group.category} className="mt-6" data-testid={`hub-group-${group.category}`}>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{group.label}</h2>
            {group.models.length === 0 ? (
              <p className="mt-2 flex items-start gap-2 text-sm text-slate-500" data-testid={`hub-empty-${group.category}`}>
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                {query.trim() ? "Tidak ada model yang cocok dengan pencarian." : (CATEGORY_NOTES[group.category] || "Belum ada model tersedia.")}
              </p>
            ) : (
              <ul className="mt-2 grid gap-3 sm:grid-cols-2">
                {group.models.map((m) => (
                  <li key={m.model}>
                    <button type="button" onClick={() => setSelected(m.model)} data-testid={`hub-model-${m.model}`}
                            className={`w-full rounded-lg border p-3 text-left hover:border-slate-400 ${selected === m.model ? "border-slate-900" : "border-slate-200"}`}>
                      <div className="font-medium text-slate-900">{m.title}</div>
                      <div className="text-xs text-slate-500">{m.provider} · {m.model}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}

      {selected && (
        <aside className="mt-6 rounded-lg border border-slate-200 p-4" data-testid="hub-detail" aria-live="polite">
          {detail.status === "loading" && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Memuat detail…</div>}
          {detail.status === "error" && <p className="text-sm text-amber-800" role="alert" data-testid="hub-detail-error">{detail.message}</p>}
          {detail.status === "ready" && detail.data && (
            <div>
              <h3 className="text-base font-semibold text-slate-900">{detail.data.title}</h3>
              <p className="text-xs text-slate-500">{categoryLabel(detail.data.category)} · {detail.data.provider}</p>
              {detail.data.description && <p className="mt-2 text-sm text-slate-700">{detail.data.description}</p>}
              <p className="mt-3 text-sm" data-testid="hub-estimate"><span className="font-medium">Estimasi biaya:</span> {estimateText(detail.data)}</p>
              {action.kind === "app" ? (
                <Button asChild className="mt-3" data-testid="hub-run-app">
                  <Link to={action.to}>{action.label} <ArrowRight className="ml-1 h-4 w-4" /></Link>
                </Button>
              ) : (
                <Button className="mt-3" disabled data-testid="hub-run-disabled">{action.label}</Button>
              )}
            </div>
          )}
        </aside>
      )}
    </div>
  );
}
